import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import styled, { StyleSheetManager } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { shouldForwardMotionProp } from './ui/shouldForwardMotionProp';

// Components
import SimpleLogin from './components/auth/SimpleLogin';
import SignUp from './components/auth/SignUp';
import ResetPassword from './components/auth/ResetPassword';
import PasswordChangeRequired from './components/auth/PasswordChangeRequired';
import AppUpdateProvider from './components/AppUpdateProvider';
import ErrorBoundary from './components/common/ErrorBoundary';
import GlobalScrollToTop from './components/common/GlobalScrollToTop';
import Dashboard from './components/dashboard/Dashboard';
import Courses from './components/courses/Courses';
import Electives from './components/electives/Electives';
import ElectiveDetail from './components/electives/ElectiveDetail';
import Assignments from './components/assignments/Assignments';
import Grades from './components/grades/Grades';
import Calendar from './components/calendar/Calendar';
import Students from './components/students/Students';
import Settings from './components/settings/Settings';
import Profile from './components/profile/Profile';
import UserManagement from './components/admin/UserManagement';
import CourseManagement from './components/admin/CourseManagement';
import ElectiveManagement from './components/admin/ElectiveManagement';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminExtensions from './components/admin/AdminExtensions';
import ExtensionRequestWizard from './pages/student/ExtensionRequestWizard';
import EnrollmentReports from './components/admin/EnrollmentReports';
import AttendanceManagement from './components/admin/AttendanceManagement';
import DeletedItemsManagement from './components/admin/DeletedItemsManagement';
import ImportExport from './components/admin/ImportExport';
import AdminPermissions from './components/admin/AdminPermissions';
import AttendanceCheckIn from './components/attendance/AttendanceCheckIn';
import CourseDetail from './components/courses/CourseDetail';
import EnrolledStudentsPage from './components/admin/EnrolledStudentsPage';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import PaymentForm from './components/payment/PaymentForm';
import CourseCatalog from './components/payment/CourseCatalog';
import EnrollmentFlow from './components/payment/EnrollmentFlow';
import PurchaseConfirmation from './components/payment/PurchaseConfirmation';
import PaymentHistoryPage from './components/payment/PaymentHistoryPage';
import DonationForm from './components/payment/DonationForm';
import DonationConfirmation from './components/payment/DonationConfirmation';
import AssignmentSubmissions from './pages/instructor/AssignmentSubmissions';
import EditAssignment from './pages/instructor/EditAssignment';
import AuthGuard from './components/auth/AuthGuard';
import RoleGuard from './components/auth/RoleGuard';
import CoursePricing from './components/admin/CoursePricing';
import PromoCodeManagement from './components/admin/PromoCodeManagement';
import BulkPricingManagement from './components/admin/BulkPricingManagement';
import AdditionalProductsManagement from './components/admin/AdditionalProductsManagement';
import PurchaseSearch from './components/admin/PurchaseSearch';
import InstructorQuizList from './pages/instructor/InstructorQuizList';
import InstructorQuizBuilder from './pages/instructor/InstructorQuizBuilder';
import QuizPreview from './pages/instructor/QuizPreview';
import QuizSubmissions from './pages/instructor/QuizSubmissions';
import InstructorSubmissionView from './pages/instructor/InstructorSubmissionView';
import AssignmentPanel from './pages/instructor/AssignmentPanel';
import Gradebook from './pages/instructor/Gradebook';
import StudentQuizList from './pages/student/StudentQuizList';
import GroupPoll from './pages/student/GroupPoll';
import QuizWarning from './pages/student/QuizWarning';
import QuizStartHandler from './pages/student/QuizStartHandler';
import QuizPlayer from './pages/student/QuizPlayer';
import MyAttendance from './pages/student/MyAttendance';
import NotesHome from './pages/student/NotesHome';
import NoteEditorPage from './pages/student/NoteEditorPage';
import ResultView from './pages/student/ResultView';
import LivePollBoard from './pages/instructor/LivePollBoard';
import InstructorPollManager from './pages/instructor/InstructorPollManager';
import logo from './assets/logo.png';

// Idle Timer Components
import useIdleTimer from './hooks/useIdleTimer';
import IdleWarningModalResponsive from './components/IdleWarningModalResponsive';
import TokenExpirationWarningModal from './components/TokenExpirationWarningModal';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: var(--background-secondary);
  width: 100%;
  
  /* Mobile simulation styles */
  .mobile-simulation & {
    max-width: var(--mobile-sim-width, 375px);
    margin: 0 auto;
    border: 2px solid var(--border-color);
    border-radius: 0;
    overflow: hidden;
    position: relative;
    min-height: 100vh;
    box-shadow: 0 0 20px rgba(0,0,0,0.3);
  }
`;

const MainContent = styled(motion.main)`
  display: flex;
  min-height: calc(100vh - 64px);
  
  @media (max-width: 768px) {
    min-height: calc(100vh - 56px);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem;
  padding-bottom: calc(2rem + 110px + env(safe-area-inset-bottom));
  background-color: var(--background-secondary);
  width: 100%;
  
  @media (max-width: 599px) {
    padding: 1rem 0;
    padding-bottom: calc(1rem + 110px + env(safe-area-inset-bottom));
  }
  
  @media (min-width: 769px) {
    padding-bottom: calc(2rem + env(safe-area-inset-bottom));
  }
`;

const AuthContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-light) 100%);
  padding: 1rem;
`;

const AuthCard = styled(motion.div)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  box-shadow: var(--shadow-md);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  
  @media (max-width: 768px) {
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 14px;
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  
  img {
    height: 60px;
    width: auto;
    margin-bottom: 0.75rem;
  }
  
  h1 {
    color: var(--primary-color);
    font-family: 'Cormorant Garamond', serif;
    font-size: 2rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
    letter-spacing: -0.025em;
  }
  
  p {
    color: var(--text-secondary);
    font-size: 0.875rem;
    font-family: 'Inter', sans-serif;
    margin: 0;
  }
`;

// Authenticated App Shell with Idle Timer
function AuthenticatedAppShell({ children, sidebarOpen, setSidebarOpen }) {
  // Note: navigate is available but not currently used in this component
  const [sessionConfig, setSessionConfig] = useState(null);

  // Fetch session configuration on mount
  useEffect(() => {
    const fetchSessionConfig = async () => {
      try {
        const response = await fetch('/portal/api/auth/session-config', {
          credentials: 'include'
        });
        const config = await response.json();
        setSessionConfig(config);
      } catch (error) {
        console.warn('Failed to fetch session config, using defaults:', error);
        // Fallback to default values
        setSessionConfig({
          idleTimeoutSeconds: 1800, // 30 minutes
          idleWarningSeconds: 60,   // 1 minute
          heartbeatMs: 60000        // 1 minute
        });
      }
    };

    fetchSessionConfig();
  }, []);


  // Don't render until we have session config
  if (!sessionConfig) {
    return (
      <AppContainer>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid var(--border-color)', 
            borderTop: '4px solid var(--primary-color)', 
            borderRadius: 0, 
            animation: 'spin 1s linear infinite' 
          }}></div>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <MainContent
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <ContentArea>
          {children}
        </ContentArea>
      </MainContent>
      {/* Scroll-to-top button removed - using GlobalScrollToTop component */}
    </AppContainer>
  );
}

// Component to handle redirects for authenticated users visiting /login
function LoginRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Get redirect URL from query parameters or localStorage
    const urlParams = new URLSearchParams(location.search);
    const redirectFromQuery = urlParams.get('redirect');
    const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
    
    const redirectUrl = redirectFromQuery || redirectFromStorage || '/dashboard';
    
    // Clear redirect from localStorage if it was used
    if (redirectFromStorage && redirectFromQuery) {
      localStorage.removeItem('faithinsight_login_redirect');
    }
    
    // Navigate to the redirect URL
    if (redirectUrl === '/catalog') {
      window.location.href = '/portal/catalog?redirect=/catalog';
    } else {
      navigate(redirectUrl, { replace: true });
    }
  }, [location.search, navigate]);
  
  return null; // This component just redirects, doesn't render anything
}

// For admins, `/dashboard` mirrors `/admin` so both URLs land on the admin
// dashboard. Everyone else gets the standard student/instructor dashboard.
function DashboardRouter() {
  const { user } = useAuth();
  return user?.role === 'admin' ? <AdminDashboard /> : <Dashboard />;
}

function AppContent() {
  const { user, loading, decodeToken, setUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sessionConfig, setSessionConfig] = useState(null);
  const [tokenExpirationWarning, setTokenExpirationWarning] = useState(false);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch session configuration on mount
  useEffect(() => {
    const fetchSessionConfig = async () => {
      try {
        const response = await fetch('/portal/api/auth/session-config', {
          credentials: 'include'
        });
        const config = await response.json();
        if (config.success) {
          setSessionConfig(config);
        } else {
          throw new Error('Failed to fetch session config');
        }
      } catch (error) {
        console.warn('Failed to fetch session config, using defaults:', error);
        // Fallback to default values
        setSessionConfig({
          success: true,
          idleTimeoutSeconds: 1800, // 30 minutes
          idleWarningSeconds: 60,   // 1 minute
          heartbeatMs: 60000        // 1 minute
        });
      }
    };

    fetchSessionConfig();
  }, []);

  // Handle auto-logout (idle timeout)
  const handleAutoLogout = async () => {
    // For idle timeout, set session_expired flag (not logged_out)
    // This shows "Your session has expired" message on login page
    localStorage.setItem('faithinsight_session_expired', 'true');
    localStorage.removeItem('faithinsight_logged_out');

    // Clear user data locally without calling API (user was already inactive)
    localStorage.removeItem('faithinsight_user');
    localStorage.removeItem('faithinsight_token');
    localStorage.removeItem('faithinsight_refresh_token');

    // Update AuthContext state
    setUser(null);

    // Navigate to login
    navigate('/login');
  };

  // Check token expiration and show warning
  // DISABLED for quiz pages: Token expiration modal is disabled during active quiz attempts
  useEffect(() => {
    if (!user || !decodeToken) return;

    const checkTokenExpiration = () => {
      // Check if user is on a quiz page - disable modal for all quiz attempts
      const isOnQuizPage = location.pathname.includes('/student/attempt/');
      
      // If on quiz page, don't show the modal (token refresh still happens silently in background)
      if (isOnQuizPage) {
        setTokenExpirationWarning(false);
        setTokenExpiresAt(null);
        return;
      }

      const token = localStorage.getItem('faithinsight_token');
      if (!token) return;

      const decoded = decodeToken(token);
      if (!decoded || !decoded.exp) return;

      const expiresAt = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      
      // Show warning 5 minutes before expiration (300000 ms)
      // Only show if NOT on a quiz page
      if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
        setTokenExpirationWarning(true);
        setTokenExpiresAt(expiresAt);
      } else {
        setTokenExpirationWarning(false);
        setTokenExpiresAt(null);
      }
    };

    // Check immediately
    checkTokenExpiration();

    // Check every 30 seconds
    const interval = setInterval(checkTokenExpiration, 30000);
    return () => clearInterval(interval);
  }, [user, decodeToken, location.pathname]);

  // Immediately close token expiration modal if user navigates to a quiz page
  useEffect(() => {
    const isOnQuizPage = location.pathname.includes('/student/attempt/');
    if (isOnQuizPage && tokenExpirationWarning) {
      setTokenExpirationWarning(false);
      setTokenExpiresAt(null);
    }
  }, [location.pathname, tokenExpirationWarning]);

  // Handle token refresh
  const handleTokenRefresh = async () => {
    try {
      const apiService = (await import('./services/api')).default;
      const response = await apiService.refreshToken();
      
      if (response.success && response.token) {
        localStorage.setItem('faithinsight_token', response.token);
        if (response.user) {
          // Update user in localStorage and context
          localStorage.setItem('faithinsight_user', JSON.stringify(response.user));
          setUser(response.user);
        }
        setTokenExpirationWarning(false);
        setTokenExpiresAt(null);
        console.log('✅ Token refreshed from warning modal');
      }
    } catch (error) {
      console.error('❌ Failed to refresh token:', error);
      // If refresh fails, logout
      handleAutoLogout();
    }
  };

  // Check for app updates on user activity (every API call)
  useEffect(() => {
    if (!user) return;

    // Check for updates when user becomes active
    const checkOnActivity = () => {
      // Import versionService dynamically to avoid circular dependencies
      import('./services/versionService').then(({ default: versionService }) => {
        if (versionService.shouldCheck()) {
          versionService.forceCheck();
        }
      });
    };

    // Check on mouse/keyboard activity (debounced)
    let activityTimeout;
    const handleActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(checkOnActivity, 5000); // Check 5 seconds after activity
    };

    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    return () => {
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      clearTimeout(activityTimeout);
    };
  }, [user]);

  // Initialize idle timer with session config only when user is logged in and config is available
  const idleTimer = useIdleTimer({
    idleTimeoutMs: sessionConfig ? sessionConfig.idleTimeoutSeconds * 1000 : 1800000, // Convert seconds to ms
    warningSeconds: sessionConfig ? sessionConfig.idleWarningSeconds : 60,
    heartbeatMs: sessionConfig ? sessionConfig.heartbeatMs : 60000,
    enabled: !!user && !!sessionConfig, // Only enable when user is logged in and config is available
    onShowWarning: () => {
      // Optional: Add any additional logic when warning is shown
    },
    onHideWarning: () => {
      // Optional: Add any additional logic when warning is hidden
    },
    onAutoLogout: handleAutoLogout
  });

  if (loading || !sessionConfig) {
    return (
      <AppContainer>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid var(--border-color)', 
            borderTop: '4px solid var(--primary-color)', 
            borderRadius: 0, 
            animation: 'spin 1s linear infinite' 
          }}></div>
        </div>
      </AppContainer>
    );
  }

  if (!user) {
    return (
      <Routes>
          {/* Public routes that don't need auth card styling */}
          <Route path="/purchase/confirmation" element={<PurchaseConfirmation />} />
          <Route path="/confirmation" element={<PurchaseConfirmation />} />
          <Route path="/donation" element={<DonationForm />} />
          <Route path="/donation/success" element={<DonationConfirmation />} />
          
          {/* Auth routes that need the auth card styling */}
          <Route path="/login" element={
            <AuthContainer>
              <AuthCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Logo>
                  <img src={logo} alt="Faith Insight Logo" />
                  <h1>Faith Insight</h1>
                  <p>Learning Portal</p>
                </Logo>
                <SimpleLogin />
              </AuthCard>
            </AuthContainer>
          } />
          <Route path="/signup" element={
            <AuthContainer>
              <AuthCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Logo>
                  <img src={logo} alt="Faith Insight Logo" />
                  <h1>Faith Insight</h1>
                  <p>Learning Portal</p>
                </Logo>
                <SignUp />
              </AuthCard>
            </AuthContainer>
          } />
          <Route path="/reset-password" element={
            <AuthContainer>
              <AuthCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Logo>
                  <img src={logo} alt="Faith Insight Logo" />
                  <h1>Faith Insight</h1>
                  <p>Learning Portal</p>
                </Logo>
                <ResetPassword />
              </AuthCard>
            </AuthContainer>
          } />
          <Route path="/attendance/checkin" element={<Navigate to="/login?redirect=%2Fattendance%2Fcheckin" replace />} />
          <Route path="/attendance/checkin/:sessionId" element={<AttendanceCheckIn />} />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
  }

  // Check if authenticated user needs to change password
  if (user && user.password_change_required) {
    return (
      <AuthContainer>
          <AuthCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Logo>
              <img src={logo} alt="Faith Insight Logo" />
              <h1>Faith Insight</h1>
              <p>Learning Portal</p>
            </Logo>
            <PasswordChangeRequired />
        </AuthCard>
      </AuthContainer>
    );
  }


  return (
    <>
    <Routes>
        {/* Special route for reset password - always shows in auth layout */}
        <Route path="/reset-password" element={
          <AuthContainer>
            <AuthCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Logo>
                <img src={logo} alt="Faith Insight Logo" />
                <h1>Faith Insight</h1>
                <p>Learning Portal</p>
              </Logo>
              <ResetPassword />
            </AuthCard>
          </AuthContainer>
        } />
        
        {/* All other authenticated routes */}
        <Route path="*" element={
          <AuthenticatedAppShell sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <AnimatePresence mode="wait">
              <Routes>
                    <Route path="/login" element={<LoginRedirect />} />
                    <Route path="/attendance/checkin" element={<MyAttendance autoCheckIn />} />
                    <Route path="/attendance/checkin/:sessionId" element={<AttendanceCheckIn />} />
                    <Route path="/catalog" element={
                      <AuthGuard>
                        <CourseCatalog />
                      </AuthGuard>
                    } />
                    <Route path="/enroll/flow/:courseIds" element={<EnrollmentFlow />} />
                    <Route path="/enroll/products/:productIds" element={<EnrollmentFlow />} />
                    <Route path="/enroll/:courseId" element={
                      <AuthGuard>
                        <ErrorBoundary>
                          <PaymentForm />
                        </ErrorBoundary>
                      </AuthGuard>
                    } />
                    <Route path="/enroll/multiple/:courseIds" element={
                      <AuthGuard>
                        <ErrorBoundary>
                          <PaymentForm />
                        </ErrorBoundary>
                      </AuthGuard>
                    } />
                    <Route path="/purchase/products/:productIds" element={
                      <AuthGuard>
                        <ErrorBoundary>
                          <PaymentForm />
                        </ErrorBoundary>
                      </AuthGuard>
                    } />
                    <Route path="/confirmation" element={<PurchaseConfirmation />} />
                    <Route path="/dashboard" element={<DashboardRouter />} />
                    <Route path="/courses" element={<Courses />} />
                    <Route path="/courses/:courseId" element={<CourseDetail />} />
                    <Route path="/electives" element={<Electives />} />
                    <Route path="/electives/:id" element={<ElectiveDetail />} />
                    <Route path="/assignments" element={
                      <AuthGuard>
                        <Assignments />
                      </AuthGuard>
                    } />
                    <Route path="/assignments/:assignmentId/submissions" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <ErrorBoundary>
                          <AssignmentSubmissions />
                        </ErrorBoundary>
                      </RoleGuard>
                    } />
                    <Route path="/assignments/:id/edit" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <EditAssignment />
                      </RoleGuard>
                    } />
                    <Route path="/grades" element={
                      <AuthGuard>
                        <Grades />
                      </AuthGuard>
                    } />
                    <Route path="/attendance" element={
                      <AuthGuard>
                        <MyAttendance />
                      </AuthGuard>
                    } />
                    <Route path="/my-attendance" element={<Navigate to="/attendance" replace />} />
                    <Route path="/calendar" element={
                      <AuthGuard>
                        <Calendar />
                      </AuthGuard>
                    } />
                    <Route path="/students" element={
                      <AuthGuard>
                        <Students />
                      </AuthGuard>
                    } />
                    <Route path="/payment-history" element={
                      <AuthGuard>
                        <PaymentHistoryPage />
                      </AuthGuard>
                    } />
                    <Route path="/donation" element={<DonationForm />} />
                    <Route path="/donation/success" element={<DonationConfirmation />} />
                    <Route path="/profile" element={
                      <AuthGuard>
                        <Profile />
                      </AuthGuard>
                    } />
                    <Route path="/settings" element={
                      <AuthGuard>
                        <Settings />
                      </AuthGuard>
                    } />
                    <Route path="/admin" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <AdminDashboard />
                      </RoleGuard>
                    } />
                    <Route path="/admin/users" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <UserManagement />
                      </RoleGuard>
                    } />
                    <Route path="/admin/courses" element={
                      <RoleGuard allowedRoles={['admin', 'instructor']}>
                        <CourseManagement />
                      </RoleGuard>
                    } />
                    <Route path="/admin/courses/:courseId/students" element={
                      <RoleGuard allowedRoles={['admin', 'instructor']}>
                        <EnrolledStudentsPage />
                      </RoleGuard>
                    } />
                    <Route path="/admin/electives/:electiveId/students" element={
                      <RoleGuard allowedRoles={['admin', 'instructor']}>
                        <EnrolledStudentsPage />
                      </RoleGuard>
                    } />
                    <Route path="/admin/electives" element={
                      <RoleGuard allowedRoles={['admin', 'instructor']}>
                        <ElectiveManagement />
                      </RoleGuard>
                    } />
                    <Route path="/admin/pricing" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <CoursePricing />
                      </RoleGuard>
                    } />
                    <Route path="/admin/promo-codes" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <PromoCodeManagement />
                      </RoleGuard>
                    } />
                    <Route path="/admin/bulk-pricing" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <BulkPricingManagement />
                      </RoleGuard>
                    } />
                    <Route path="/admin/additional-products" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <AdditionalProductsManagement />
                      </RoleGuard>
                    } />
                    <Route path="/admin/purchase-lookup" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <PurchaseSearch />
                      </RoleGuard>
                    } />
                    <Route path="/admin/reports" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <EnrollmentReports />
                      </RoleGuard>
                    } />
                    <Route path="/extensions/new" element={
                      <AuthGuard>
                        <ExtensionRequestWizard />
                      </AuthGuard>
                    } />
                    <Route path="/admin/extensions" element={
                      <RoleGuard allowedRoles={['admin', 'instructor']}>
                        <AdminExtensions />
                      </RoleGuard>
                    } />
                    <Route path="/admin/attendance" element={
                      <AuthGuard>
                        <AttendanceManagement />
                      </AuthGuard>
                    } />
                    <Route path="/admin/deleted-items" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <DeletedItemsManagement />
                      </RoleGuard>
                    } />
                    <Route path="/admin/import-export" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <ImportExport />
                      </RoleGuard>
                    } />
                    <Route path="/admin/permissions" element={
                      <RoleGuard allowedRoles={['admin']}>
                        <AdminPermissions />
                      </RoleGuard>
                    } />
                    <Route path="/instructor/polls" element={
                      <AuthGuard>
                        <InstructorPollManager />
                      </AuthGuard>
                    } />
                    <Route path="/instructor/live-poll" element={
                      <AuthGuard>
                        <LivePollBoard />
                      </AuthGuard>
                    } />
                    
                    {/* Quiz Routes */}
                    <Route path="/instructor/quizzes" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <InstructorQuizList />
                      </RoleGuard>
                    } />
                    <Route path="/instructor/quizzes/new" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <ErrorBoundary>
                          <InstructorQuizBuilder />
                        </ErrorBoundary>
                      </RoleGuard>
                    } />
                    <Route path="/instructor/quizzes/:quizId/edit" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <ErrorBoundary>
                          <InstructorQuizBuilder />
                        </ErrorBoundary>
                      </RoleGuard>
                    } />
                    <Route path="/instructor/quizzes/:quizId/preview" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <QuizPreview />
                      </RoleGuard>
                    } />
                    <Route path="/instructor/quizzes/:quizId/attempts" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <ErrorBoundary>
                          <QuizSubmissions />
                        </ErrorBoundary>
                      </RoleGuard>
                    } />
                    <Route path="/instructor/submissions/:attemptId" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <InstructorSubmissionView />
                      </RoleGuard>
                    } />
                    <Route path="/instructor/quizzes/:quizId/assign" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <AssignmentPanel />
                      </RoleGuard>
                    } />
                    <Route path="/instructor/quizzes/:quizId" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <QuizPreview />
                      </RoleGuard>
                    } />
                    <Route path="/instructor/gradebook" element={
                      <RoleGuard allowedRoles={['instructor', 'admin']}>
                        <Gradebook />
                      </RoleGuard>
                    } />
                    <Route path="/student/quizzes" element={
                      <AuthGuard>
                        <StudentQuizList />
                      </AuthGuard>
                    } />
                    <Route path="/student/group-poll" element={
                      <AuthGuard>
                        <GroupPoll />
                      </AuthGuard>
                    } />
                    <Route path="/student/assignments/:assignmentId/warning" element={
                      <AuthGuard>
                        <QuizWarning />
                      </AuthGuard>
                    } />
                    <Route path="/student/assignments/:assignmentId/start" element={
                      <AuthGuard>
                        <QuizStartHandler />
                      </AuthGuard>
                    } />
                    <Route path="/student/attempt/:attemptId" element={
                      <AuthGuard>
                        <ErrorBoundary>
                          <QuizPlayer />
                        </ErrorBoundary>
                      </AuthGuard>
                    } />
                    <Route path="/student/result/:attemptId" element={
                      <AuthGuard>
                        <ErrorBoundary>
                          <ResultView />
                        </ErrorBoundary>
                      </AuthGuard>
                    } />
                    <Route path="/student/notes" element={
                      <AuthGuard>
                        <NotesHome />
                      </AuthGuard>
                    } />
                    <Route path="/student/notes/new" element={
                      <AuthGuard>
                        <NoteEditorPage />
                      </AuthGuard>
                    } />
                    <Route path="/student/notes/:noteId" element={
                      <AuthGuard>
                        <NoteEditorPage />
                      </AuthGuard>
                    } />
                    
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </AnimatePresence>
          </AuthenticatedAppShell>
        } />
      </Routes>
      
      {/* Global Scroll-to-Top FAB - Single instance for entire app */}
      <GlobalScrollToTop />
      
      {/* Idle Warning Modal */}
      <IdleWarningModalResponsive
        open={idleTimer.warningVisible}
        logoutAtMs={idleTimer.logoutAtMs}
        onStay={idleTimer.stayLoggedIn}
        onLogout={handleAutoLogout}
      />
      {/* Token Expiration Warning Modal */}
      <TokenExpirationWarningModal
        open={tokenExpirationWarning}
        expiresAtMs={tokenExpiresAt}
        onRefresh={handleTokenRefresh}
        onDismiss={() => setTokenExpirationWarning(false)}
      />
    </>
  );
}

function App() {
  // Mobile viewport height fix
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // Unregister any existing service workers to prevent FetchEvent.respondWith errors
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          console.log('Unregistering service worker:', registration.scope);
          registration.unregister();
        });
      });
    }
  }, []);

  // Service worker disabled - was causing FetchEvent.respondWith errors
  // useEffect(() => {
  //   const initWorkbox = async () => {
  //     try {
  //       await workboxService.init();
  //       // Workbox initialized successfully
  //     } catch (error) {
  //       // Failed to initialize Workbox
  //     }
  //   };

  //   // Only initialize in production
  //   if (process.env.NODE_ENV === 'production') {
  //     initWorkbox();
  //   }

  //   // Listen for service worker update events
  //   const handleSWUpdate = (event) => {
  //     // Service worker update available
  //     // You can show a notification to the user here
  //   };

  //   window.addEventListener('sw-update-available', handleSWUpdate);

  //   return () => {
  //     window.removeEventListener('sw-update-available', handleSWUpdate);
  //   };
  // }, []);

  return (
    <ErrorBoundary>
      <StyleSheetManager shouldForwardProp={shouldForwardMotionProp}>
        <ThemeProvider>
          <AuthProvider>
            <AppUpdateProvider>
              <Router basename="/portal">
                <ErrorBoundary>
                  <AppContent />
                </ErrorBoundary>
              </Router>
            </AppUpdateProvider>
          </AuthProvider>
        </ThemeProvider>
      </StyleSheetManager>
    </ErrorBoundary>
  );
}

export default App;
